import React, {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import GoogleLogoIcon from "src/assets/icons/google_logo.svg?react";
import MSWindowsLogoIcon from "src/assets/icons/microsoft-window.svg?react";
import CleverLogoIcon from "src/assets/icons/clever.svg?react";
import CheckMarkIcon from "src/assets/icons/check_mark.svg?react";
import { useHistory } from "react-router-dom";
import styles from "./AccountRegistration.module.scss";
import { TextInput } from "@src/components/text-input/TextInput";
import {
  useAccountQuery,
  useSchoolQuery,
  useRegistrationMutation,
} from "@api/graphql-codegen";
import { hasError } from "@src/utils/validation";
import Checkbox from "@src/components/checkbox/Checkbox";
import Select from "@src/components/select/Select";
import { SelectOption } from "@src/types";
import { useRegistrationForm, transformToAccountForm } from "./utils";
import useGoogleAuth from "@src/hooks/useGoogleAuth";
import useMicrosoftAuth from "@src/hooks/useMicrosoftAuth";
import useCleverAuth from "@src/hooks/useCleverAuth";
import NormalModal from "@src/components/modal/normal-modal/NormalModal";
import IconWarning from "@src/assets/icons/modal-warning.svg?react";
import Button from "@components/button/Button";
import ArrowIcon from "src/assets/icons/curvearrow_white.svg?react";
import RegistrationPageLayout from "@src/components/auth-page-layout/RegistrationPageLayout";
import { Events, useAnalytics } from "@src/hooks";

export default function AccountRegistration({
  entity,
  entityId,
}: {
  entity: "acc" | "sch";
  entityId: string;
}) {
  const { push } = useHistory();
  const [registrationSuccessful, setRegistrationSuccessful] = useState(false);
  const { form, formErrors, resetForm, populateForm, handleFieldChange } =
    useRegistrationForm(entity === "acc" ? "Account" : "School");
  const [formSubmitting, setFormSubmitting] = useState(false);
  const { data: accountData, loading: accountLoading } = useAccountQuery({
    variables: { input: { id: `${entityId}` } },
    skip: entity !== "acc",
  });
  const { data: schoolData, loading: schoolLoading } = useSchoolQuery({
    variables: { input: { id: `${entityId}` } },
    skip: entity !== "sch",
  });
  const entityLoading = accountLoading || schoolLoading;
  const { googleAuth, googleAuthUser, setGoogleUser, googleAuthLoading } =
    useGoogleAuth("Registration");
  const {
    microsoftAuth,
    microsoftUser,
    setMicrosoftUser,
    microsoftAuthLoading,
  } = useMicrosoftAuth("Registration");
  const { cleverAuth, setCleverUser, cleverUser } =
    useCleverAuth("Registration");
  const [withGoogle, setWithGoogle] = useState(false);
  const [withMicrosoft, setWithMicrosoft] = useState(false);
  const [withClever, setWithClever] = useState(false);
  const [isRegisteredEmail, setIsRegisteredEmail] = useState(false);

  const { trackEvent } = useAnalytics();

  const [registration, { loading: sending, error: registrationError }] =
    useRegistrationMutation({
      onCompleted(data) {
        if (data?.registration.token) {
          const d = { email: data?.registration.user?.email, loginType: 'Email', userId: data.registration.user?.id };
          trackEvent(Events.actions.logIn, d);
          setRegistrationSuccessful(true);
        }
      },
      onError(error) {
        if (
          (error?.message ?? "").includes("This email is already registered.")
        ) {
          setIsRegisteredEmail(true);
        }
      },
    });
  const schoolOptions = (accountData?.account ?? []).map((n) => ({
    label: n?.name,
    value: n?.id,
  })) as SelectOption[];

  useEffect(() => {
    if (
      (entity === "acc" && accountData && !accountData.account?.length) ||
      (entity === "sch" && schoolData && !schoolData.school)
    ) {
      push("/expiryRedirect");
    }
  }, [accountData, entity, push, schoolData]);

  const isValidGoogleUser = useMemo(() => {
    if (!googleAuthUser || !googleAuthUser.email) return false;

    setWithGoogle(true);
    populateForm(googleAuthUser);
    return true;
  }, [googleAuthUser, populateForm]);

  const isValidMicrosoftUser = useMemo(() => {
    if (!microsoftUser || !microsoftUser.email) return false;

    setWithMicrosoft(true);
    populateForm(microsoftUser);
    return true;
  }, [microsoftUser, populateForm]);

  const isValidCleverUser = useMemo(() => {
    if (!cleverUser || !cleverUser.email) return false;

    setWithClever(true);
    populateForm({
      family_name: cleverUser.firstName,
      given_name: cleverUser.lastName,
      email: cleverUser.email,
    });
    return true;
  }, [cleverUser, populateForm]);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setFormSubmitting(true);

      if (!hasError(formErrors)) {
        await registration({
          variables: {
            input: {
              id: entityId,
              entity,
              ...transformToAccountForm(form),
            },
          },
        });
      }
    },
    [entity, entityId, form, formErrors, registration]
  );

  return (
    <RegistrationPageLayout
      loading={entityLoading || googleAuthLoading || microsoftAuthLoading || sending}
      onSubmit={handleSubmit}
      buttonTitle={"Register"}
      title={"Registration"}
      registrationSuccessful={registrationSuccessful}
    >
      {(
        <>
          {registrationSuccessful ? (
            <div className={styles.accepted}>
              <div className={styles.acceptedTitle}>Thank you!</div>
              <div className={styles.acceptedSubtitle}>
                Please check your inbox for further instructions.
              </div>
            </div>
          ) : (
            <div>
            <div className={styles.formContent}>
              {!isValidGoogleUser &&
                !isValidMicrosoftUser &&
                !isValidCleverUser && (
                  <>
                    <div>
                      <div className={styles.gWrapper}>
                        <div className={styles.titleView}>{'Register with Single Sign-On:'}</div>
                        <div
                          className={styles.gInput}
                          role="button"
                          aria-pressed="false"
                          onClick={() => googleAuth()}
                        >
                          <div className={styles.iconBackground}>
                            <div className={styles.iconView}>
                              <GoogleLogoIcon />
                            </div>
                          </div>
                          <span className={styles.gLabel}>
                            Register with Google
                          </span>
                        </div>
                        <div
                          className={styles.msInput}
                          role="button"
                          aria-pressed="false"
                          onClick={() => microsoftAuth()}
                        >
                          <div className={styles.iconBackground}>
                            <div className={styles.iconView}>
                              <MSWindowsLogoIcon width={18} />
                            </div>
                          </div>
                          <span className={styles.msLabel}>
                            Register with Microsoft
                          </span>
                        </div>
                        <div
                          className={styles.cleverInput}
                          role="button"
                          aria-pressed="false"
                          onClick={() => {
                            cleverAuth();
                          }}
                        >
                          <div className={styles.iconBackground}>
                            <div className={styles.iconView}>
                              <CleverLogoIcon />
                            </div>
                          </div>
                          <span className={styles.cleverLabel}>
                            Register with Clever
                          </span>
                        </div>
                        {(withGoogle ||
                        withMicrosoft ||
                        withClever) ? (
                          <div className={styles.eNoteBlock}>
                            <div className={styles.noteBlock_eLabel1}><strong>Note: </strong>{`Your ${
                              withGoogle
                                ? "Google"
                                : withMicrosoft
                                ? "Microsoft"
                                : withClever
                                ? "Clever"
                                : ""
                            } email must match:`}</div>
                            <div className={styles.noteBlock_eValue}>{form.email}</div>
                            <div className={styles.noteBlock_eLabel}>
                              If you think this is an error, please contact your Open
                              Parachute Administrator and ask them to update your email
                              address.
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <div className={styles.formWrapper}>
                      <div className={styles.titleView} style={{marginBottom: 20}}>{'Register with Email:'}</div>
                      <div className={styles.formField}>
                        <div className={styles.emailRow}>
                          <TextInput
                            allowSpecialCharacters
                            hasError={formSubmitting && !!formErrors["email"]}
                            subTitle={formSubmitting && formErrors["email"]}
                            value={form.email}
                            onChange={(val) => handleFieldChange("email", val)}
                            label={"Email *"}
                            id={"email"}
                            placeholder={"Email"}
                            name="email"
                          />
                        </div>
                      </div>
                      <div className={styles.formField}>
                        <div className={styles.row}>
                          <TextInput
                            hasError={
                              formSubmitting && !!formErrors["firstName"]
                            }
                            subTitle={formSubmitting && formErrors["firstName"]}
                            value={form.firstName}
                            onChange={(val) =>
                              handleFieldChange("firstName", val)
                            }
                            label={"First Name"}
                            id={"firstName"}
                            placeholder={"First Name"}
                            name="firstName"
                            className={styles.userNameInput}
                          />
                          <TextInput
                            hasError={
                              formSubmitting && !!formErrors["lastName"]
                            }
                            subTitle={formSubmitting && formErrors["lastName"]}
                            value={form.lastName}
                            onChange={(val) =>
                              handleFieldChange("lastName", val)
                            }
                            label={"Last Name"}
                            id={"lastName"}
                            placeholder={"Last Name"}
                            name="lastName"
                            className={styles.userNameInput}
                          />
                        </div>
                      </div>

                      <div className={styles.formField}>
                        <div className={styles.row}>
                          <TextInput
                            allowSpecialCharacters
                            hasError={formSubmitting && !!formErrors["password"]}
                            subTitle={formSubmitting ? formErrors["password"] : ""}
                            type="password"
                            value={form.password}
                            onChange={(val) => handleFieldChange("password", val)}
                            label={"Password *"}
                            id={"password"}
                            placeholder={"Password"}
                            className={styles.userNameInput}
                          />
                          <TextInput
                            allowSpecialCharacters
                            hasError={
                              formSubmitting && !!formErrors["rePassword"]
                            }
                            subTitle={
                              formSubmitting ? formErrors["rePassword"] : ""
                            }
                            type="password"
                            value={form.rePassword}
                            onChange={(val) =>
                              handleFieldChange("rePassword", val)
                            }
                            label={"Retype Password *"}
                            id={"confirmPassword"}
                            placeholder={"Password"}
                            className={styles.userNameInput}
                          />
                        </div>
                      </div>
                      <div>
                        <span className={styles.passwordNote}>
                          Password should have at least <strong>one uppercase</strong> letter, <strong>one lowercase</strong> letter, <strong>one digit, one special character</strong> and be at least <strong>8 characters long.</strong>
                        </span>
                      </div>
                    </div>
                  </>
                )}
              {isValidGoogleUser && (
                <>
                  <div style={{width: '100%'}}>
                    <div className={styles.cNoteBlock}>
                      <div className={styles.gcLabel}>
                        <div className={styles.inline}>
                          <div className={styles.rounded}>
                            <GoogleLogoIcon width={20} />
                          </div>
                          <span style={{color: "#AF77B8"}}>
                            Connected with: <br/>
                            <span style={{ fontWeight: "700", color: "#274649" }}>
                              {googleAuthUser?.email}
                            </span>
                          </span>
                        </div>
                        <CheckMarkIcon />
                      </div>
                    </div>
                  </div>
                </>
              )}
              {isValidMicrosoftUser && (
                <>
                  <div>
                    <div className={styles.cNoteBlock}>
                      <div className={styles.gcLabel}>
                        <div className={styles.inline}>
                          <div className={styles.rounded}>
                            <MSWindowsLogoIcon width={20} />
                          </div>
                          <span style={{color: "#AF77B8"}}>
                            Connected with: <br/>
                            <span style={{ fontWeight: "700", color: "#274649" }}>
                              {microsoftUser?.email}
                            </span>
                          </span>
                        </div>
                        <CheckMarkIcon />
                      </div>
                    </div>
                  </div>
                </>
              )}
              {isValidCleverUser && (
                <>
                  <div>
                    <div className={styles.cNoteBlock}>
                      <div className={styles.gcLabel}>
                        <div className={styles.inline}>
                          <div className={styles.rounded}>
                            <CleverLogoIcon width={20} />
                          </div>
                          <span style={{color: "#AF77B8"}}>
                            Connected with: <br/>
                            <span style={{ fontWeight: "700", color: "#274649" }}>
                              {cleverUser?.email}
                            </span>
                          </span>
                        </div>
                        <CheckMarkIcon />
                      </div>
                    </div>
                  </div>
                </>
              )}

              {formSubmitting && !!registrationError?.message && (
                <div className={styles.serverError}>
                  {registrationError.message}
                </div>
              )}
              <div className={styles.arrowDiv}>
                <span className={styles.arrowText}>
                  Need help logging in?
                  <br />
                  Click to Chat with us!
                </span>
                <ArrowIcon width={"40"} height={"200"} />
              </div>
            </div>
            {entity === "acc" && (
              <div className={styles.schoolSelection} style={{marginTop: 20}}>
                <Select
                  error={formSubmitting && !!formErrors["school"]}
                  subTitle={
                    formSubmitting && formErrors["school"]
                      ? formErrors["school"]
                      : ""
                  }
                  disabled={
                    accountLoading || schoolOptions?.length === 0
                  }
                  label={"Select your School *"}
                  options={schoolOptions}
                  value={form.school}
                  loading={accountLoading}
                  onChange={(option) =>
                    handleFieldChange("school", option.value)
                  }
                  searchAutoFocus
                  className={styles.schoolView}
                />
              </div>
            )}
            <div className={styles.touWrapper} style={{flexDirection: isValidGoogleUser || isValidMicrosoftUser || isValidCleverUser ? 'column' : 'row', gap: isValidGoogleUser || isValidMicrosoftUser || isValidCleverUser ? 0 : '40px'}}>
              <div>
                <Checkbox
                  id={"tou"}
                  checked={form.acceptTerm}
                  onChange={(val) => handleFieldChange("acceptTerm", val)}
                >
                  <span className={styles.touText}>
                    I have read and agree to the{" "}
                    <a
                      className={styles.touLink}
                      onClick={() => {
                        window.open(
                          "https://openparachuteschools.com/terms-of-use-app/",
                          "_blank"
                        );
                      }}
                      target="_blank"
                      rel="noreferrer"
                      href="https://openparachuteschools.com/terms-of-use-app/"
                    >
                      Terms of Use
                    </a>
                  </span>
                </Checkbox>
                {formSubmitting && !!formErrors["acceptTerm"] && (
                  <div className={styles.serverError}>
                    {formErrors["acceptTerm"]}
                  </div>
                )}
              </div>
              <div>
                <Checkbox
                  id={"policy"}
                  checked={form.acceptPolicy}
                  onChange={(val) => handleFieldChange("acceptPolicy", val)}
                >
                  <span className={styles.touText}>
                    I agree to the{" "}
                    <a
                      className={styles.touLink}
                      onClick={() => {
                        window.open(
                          "https://openparachuteschools.com/privacy-policy/",
                          "_blank"
                        );
                      }}
                      target="_blank"
                      rel="noreferrer"
                      href="https://openparachuteschools.com/privacy-policy/"
                    >
                      Privacy Policy
                    </a>
                  </span>
                </Checkbox>
                {formSubmitting && !!formErrors["acceptPolicy"] && (
                  <div className={styles.serverError}>
                    {formErrors["acceptPolicy"]}
                  </div>
                )}
              </div>
            </div>
          </div>
          )}
        </>
      )}
      <NormalModal
        logoIcon={<IconWarning />}
        title={"This email already exists."}
        open={isRegisteredEmail && (withGoogle || withMicrosoft || withClever)}
        description="This email is associated with another Open Parachute account. Please select from following:"
        leftButton={
          <Button
            size="small"
            className={styles.cancelButton}
            onClick={() => {
              resetForm();
              setWithGoogle(false);
              setWithMicrosoft(false);
              setGoogleUser(undefined);
              setMicrosoftUser(undefined);
              setCleverUser(undefined);
              setWithClever(false);
              setFormSubmitting(false);
              setIsRegisteredEmail(false);
            }}
            label="REGISTER WITH NEW EMAIL"
          />
        }
        rightButton={
          <Button
            size="small"
            className={styles.actionButton}
            onClick={() => push("/login")}
            label="LOGIN WITH EMAIL"
          />
        }
      />
    </RegistrationPageLayout>
  );
}
